/**
 * 我的電子券區塊
 * 列出會員的電子券，有效券顯示 QR Code（內容為券碼），供核銷端掃描
 */

import { useState } from 'react';
import useSWR from 'swr';
import { QRCodeSVG } from 'qrcode.react';
import { SectionCard } from './SectionCard';
import { Loading } from './Loading';
import {
  getCustomerVouchers,
  type CustomerVoucherItem,
} from '../api/memberClient';
import { formatInTimeZone } from 'date-fns-tz';
import { Ticket, ChevronDown, ChevronUp } from 'lucide-react';

const TAIWAN_TZ = 'Asia/Taipei';

function VoucherCard({ voucher }: { voucher: CustomerVoucherItem }) {
  const [showQr, setShowQr] = useState(false);
  const isValid = voucher.status === 'valid';
  const expiredAtStr =
    voucher.expires_at &&
    formatInTimeZone(new Date(voucher.expires_at), TAIWAN_TZ, 'yyyy/MM/dd');
  const applicableStr = voucher.applicable_date
    ? `${voucher.applicable_date}${voucher.applicable_time_start && voucher.applicable_time_end ? ` ${voucher.applicable_time_start}～${voucher.applicable_time_end}` : ''}`
    : null;

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Ticket className="shrink-0 w-5 h-5 text-gray-500" />
            <span className="font-medium text-gray-900">電子券</span>
            <span
              className={`text-xs px-2 py-0.5 rounded ${
                voucher.status === 'valid'
                  ? 'bg-green-100 text-green-800'
                  : voucher.status === 'redeemed'
                    ? 'bg-gray-100 text-gray-600'
                    : 'bg-red-100 text-red-800'
              }`}
            >
              {voucher.status === 'valid'
                ? '有效'
                : voucher.status === 'redeemed'
                  ? '已核銷'
                  : '已過期'}
            </span>
          </div>
          {expiredAtStr && (
            <p className="text-sm text-gray-500 mt-1">過期日：{expiredAtStr}</p>
          )}
          {applicableStr && (
            <p className="text-sm text-gray-600 mt-0.5">
              適用：{applicableStr}
            </p>
          )}
        </div>
        {isValid && (
          <button
            type="button"
            onClick={() => setShowQr(!showQr)}
            className="shrink-0 flex items-center gap-1 text-sm text-teal-600 hover:text-teal-800"
          >
            {showQr ? (
              <>
                收起 QR <ChevronUp className="w-4 h-4" />
              </>
            ) : (
              <>
                出示 QR <ChevronDown className="w-4 h-4" />
              </>
            )}
          </button>
        )}
      </div>
      {isValid && showQr && (
        <div className="mt-4 flex flex-col items-center">
          <p className="text-xs text-gray-500 mb-2">
            請出示此 QR 供店家掃描核銷
          </p>
          <div className="bg-white p-3 rounded-lg border border-gray-200 inline-block">
            <QRCodeSVG value={voucher.code} size={180} level="M" />
          </div>
          <p className="text-xs text-gray-400 mt-2 font-mono">{voucher.code}</p>
        </div>
      )}
    </div>
  );
}

interface VouchersBlockProps {
  customerId: string;
}

export function VouchersBlock({ customerId }: VouchersBlockProps) {
  const { data, isLoading, error } = useSWR(
    customerId ? ['customer-vouchers', customerId] : null,
    () => getCustomerVouchers(customerId, { includeExpired: true, limit: 50 }),
    { revalidateOnFocus: false, dedupingInterval: 60000 }
  );

  const vouchers = data?.data ?? [];
  const validCount = vouchers.filter(v => v.status === 'valid').length;

  return (
    <SectionCard title="我的電子券">
      {isLoading && (
        <div className="py-6">
          <Loading />
        </div>
      )}
      {error && (
        <p className="text-sm text-red-600">無法載入電子券，請稍後再試。</p>
      )}
      {!isLoading && !error && vouchers.length === 0 && (
        <p className="text-sm text-gray-500">目前沒有電子券。</p>
      )}
      {!isLoading && !error && vouchers.length > 0 && (
        <div className="space-y-3">
          {validCount > 0 && (
            <p className="text-sm text-gray-600">
              共 {vouchers.length} 張，其中 {validCount} 張可出示核銷
            </p>
          )}
          {vouchers.map(v => (
            <VoucherCard key={v.id} voucher={v} />
          ))}
        </div>
      )}
    </SectionCard>
  );
}
